import React, { useEffect, useRef, useState } from "react";
import { Loader2, Users } from "lucide-react";
import { toast } from "sonner";
import { getTogether, joinTogether } from "../soulumoPractice/practiceApi";
import { SaveGiftButton } from "../../pages/Share/SaveGiftButton";
import { TogetherRhythm } from "./TogetherRhythm";

const SANS = '"Manrope", sans-serif';
const SERIF = '"Cormorant Garamond", serif';
const token = () => localStorage.getItem("reclaim_token");
const fmt = (d) => new Date(`${d}T12:00:00`).toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" });

// Under the gifted installation: join the shared 21 days, or just keep the installation.
export const TogetherInvite = ({ id, slug }) => {
  const [inv, setInv] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const ran = useRef(false);
  useEffect(() => { getTogether(id).then(setInv).catch(() => setInv(false)); }, [id]);
  const join = async (replace = false) => {
    if (!token()) { window.location.href = `/soulumo/join?next=${encodeURIComponent(`/share/audio/${slug}?b=soulumo&t=${id}&join=1`)}`; return; }
    setBusy(true);
    try {
      const d = await joinTogether(id, replace);
      window.location.href = `/my-library?open=${d.journey_id}&together=1`;
    } catch (e) {
      const code = e?.response?.data?.detail;
      if (code === "has_plan") setConfirm(true);
      else toast.error(code === "invite_closed" ? "This invitation has closed. You can still keep the installation." : "Couldn't join just now. Please try again.");
      setBusy(false);
    }
  };
  useEffect(() => {
    if (ran.current || !inv || !token() || new URLSearchParams(window.location.search).get("join") !== "1") return;
    ran.current = true; join();
  }, [inv]); // eslint-disable-line react-hooks/exhaustive-deps
  if (inv === null) return null;
  if (inv === false || inv.closed || inv.over) return <SaveGiftButton slug={slug} />;
  const when = inv.begun ? `Today is Day ${inv.day} of 21.` : `It begins on ${fmt(inv.start_date)}.`;
  return (
    <div className="mt-6 rounded-2xl p-5 text-left" style={{ border: "1px solid rgba(224,169,122,0.45)", background: "rgba(224,169,122,0.06)" }} data-testid="together-invite">
      <p className="text-[0.6rem] uppercase tracking-[0.3em]" style={{ color: "#e0a97a", fontFamily: SANS }}><Users size={11} className="mr-1 inline -mt-0.5" /> 21 Days Together</p>
      <h3 className="mt-2 text-2xl" style={{ color: "#f5ecd9", fontFamily: SERIF }}>You've been invited to 21 Days Together</h3>
      <p className="mt-2 text-sm leading-relaxed" style={{ color: "#e9e2d0", fontFamily: SANS }} data-testid="together-invite-copy">
        {inv.owner_name} has shared this installation with you and invited you to experience it together for 21 days. You'll follow the same listening rhythm, while Soulumo takes care of your own reminders and planner.
      </p>
      <p className="mt-2 text-xs" style={{ color: "rgba(245,236,217,0.6)", fontFamily: SANS }} data-testid="together-invite-when">{when}</p>
      <TogetherRhythm intensity={inv.intensity} night={inv.night} day={inv.day} />
      {confirm ? (
        <div className="mt-4" data-testid="together-replace-confirm">
          <p className="text-sm" style={{ color: "#f5ecd9", fontFamily: SANS }}>You already have a 21-day plan running. Switch to this shared one? Your current installation stays in your library.</p>
          <div className="mt-3 flex gap-3">
            <button type="button" onClick={() => join(true)} disabled={busy} data-testid="together-replace-yes" className="flex-1 rounded-full py-3 text-xs uppercase tracking-[0.22em]" style={{ background: "#e0a97a", color: "#0b1430", fontFamily: SANS }}>{busy ? <Loader2 size={14} className="mx-auto animate-spin" /> : "Switch"}</button>
            <button type="button" onClick={() => setConfirm(false)} data-testid="together-replace-no" className="flex-1 rounded-full py-3 text-xs uppercase tracking-[0.22em]" style={{ border: "1px solid rgba(224,169,122,0.5)", color: "#e0a97a", fontFamily: SANS }}>Keep mine</button>
          </div>
        </div>
      ) : (
        <button type="button" onClick={() => join()} disabled={busy} data-testid="together-join-btn"
          className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full py-3.5 text-[0.72rem] uppercase tracking-[0.26em] transition-opacity hover:opacity-90 disabled:opacity-50" style={{ background: "#e0a97a", color: "#0b1430", fontFamily: SANS }}>
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Users size={14} />} Join the 21 Days
        </button>
      )}
      <SaveGiftButton slug={slug} label="Save installation only" secondary />
    </div>
  );
};
